import { useEffect, useRef, useState } from 'react';

function CountUp({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !startedRef.current) {
          startedRef.current = true;
          let start = 0;
          const step = target / 60;
          const interval = setInterval(() => {
            start += step;
            if (start >= target) {
              setCount(target);
              clearInterval(interval);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function TechHighlight() {
  const whatsapp = (msg: string) =>
    `https://wa.me/905412190629?text=${encodeURIComponent(msg)}`;

  return (
    <section id="teknoloji" className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.3em] mb-3 block">Özel Teknoloji</span>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4">
            Geleceğin Güzellik
            <br />
            <em className="text-[#D4AF37]">Teknolojileri</em>
          </h2>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mt-4" />
        </div>

        {/* EMS Slim */}
        <div className="relative rounded-3xl overflow-hidden mb-12">
          <img
            src="https://readdy.ai/api/search-image?query=EMS%20slim%20electromagnetic%20muscle%20stimulation%20technology%20at%20premium%20beauty%20clinic%2C%20woman%20receiving%20high-tech%20body%20treatment%20on%20modern%20white%20medical%20bed%2C%20digital%20panels%20glowing%2C%20futuristic%20medical%20spa%20with%20champagne%20gold%20and%20pink%20ambiance&width=1400&height=600&seq=ems_tech001&orientation=landscape"
            alt="EMS Slim"
            className="w-full h-72 md:h-[420px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-2xl px-8 md:px-16">
              <span className="inline-block bg-[#D4AF37] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
                EMS Slim Teknolojisi
              </span>
              <h3 className="font-serif text-white text-3xl md:text-5xl font-bold mb-3 leading-tight">
                30 Dakikada
                <br />
                <span className="text-[#D4AF37]">
                  <CountUp target={20000} /> Kas
                </span>
                <br />
                Aktivasyonu
              </h3>
              <p className="text-white/80 text-sm md:text-base mb-6 max-w-md leading-relaxed">
                Yüksek yoğunluklu elektromanyetik alan teknolojisi ile kaslarınızı supra-maximal kasılmaya zorlar. 
                Egzersizin imkânsız erişemeyeceği sonuçlar.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                {[
                  { val: '30', unit: 'Dakika', label: 'seans süresi' },
                  { val: '%19', unit: '', label: 'yağ azalması' },
                  { val: '%16', unit: '', label: 'kas artışı' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 text-center">
                    <p className="text-white font-bold text-xl">{stat.val}<span className="text-[#D4AF37] text-sm">{stat.unit}</span></p>
                    <p className="text-white/70 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
              <a
                href={whatsapp('EMS Slim tedavisi hakkında bilgi almak istiyorum.')}
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8962E] transition-all hover:-translate-y-0.5 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                EMS Slim Hakkında Bilgi Al
              </a>
            </div>
          </div>
        </div>

        {/* ARC */}
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=ARC%20skin%20renewal%20non-surgical%20treatment%20technology%20at%20luxury%20beauty%20clinic%2C%20woman%20receiving%20radiofrequency%20facial%20rejuvenation%20with%20glowing%20healthy%20radiant%20skin%20result%2C%20advanced%20beauty%20technology%20device%2C%20premium%20champagne%20gold%20and%20rose%20aesthetic%20clinic%20interior&width=1400&height=600&seq=arc_tech001&orientation=landscape"
            alt="ARC Cilt Yenileme"
            className="w-full h-72 md:h-[420px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-black/75 via-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-end">
            <div className="max-w-2xl px-8 md:px-16 text-right">
              <span className="inline-block bg-[#FFB3C6] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
                ARC Teknolojisi
              </span>
              <h3 className="font-serif text-white text-3xl md:text-5xl font-bold mb-3 leading-tight">
                Cerrahi Olmadan
                <br />
                <span className="text-[#FFB3C6]">Cilt Yenileme</span>
                <br />
                Teknolojisi
              </h3>
              <p className="text-white/80 text-sm md:text-base mb-6 max-w-md ml-auto leading-relaxed">
                Radyofrekans ve ultrason kombinasyonu ile deri altındaki kollajen üretimini aktive eder. 
                Yıllar içinde kaybettiğiniz sıkılık ve tazelik geri döner.
              </p>
              <div className="flex flex-wrap gap-4 mb-6 justify-end">
                {[
                  { val: '0', unit: '', label: 'Ameliyat yok' },
                  { val: '0', unit: '', label: 'Bekleme süresi' },
                  { val: '95%', unit: '', label: 'Memnuniyet' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 text-center">
                    <p className="text-white font-bold text-xl">{stat.val}<span className="text-[#FFB3C6] text-sm">{stat.unit}</span></p>
                    <p className="text-white/70 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
              <a
                href={whatsapp('ARC Cilt Yenileme tedavisi hakkında bilgi almak istiyorum.')}
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 bg-[#FFB3C6] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#FF8FA7] transition-all hover:-translate-y-0.5 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                ARC Hakkında Bilgi Al
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
